/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { RoleEnum } from "@/enum/role";
import { useOperatorMeQuery, useOperatorMePublicQuery } from "@/services/user";
import { getUserFromLocalStorage } from "@/utils/getUserFromLocalStorage";
import { useAuthData } from "@/context/auth-data-provider";
import { apiSlice } from "@/services/http";
import { Load } from "components/load";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import PageError from "../components/page-error";
import { checkPermission } from "../shared/handle-permission";
import { excludedRoutes } from "./public-route-map";
import { featureFlags } from "@/config/feature-flag";

const AccessContext = createContext<{
  isPermission: boolean;
} | null>(null);

export const AccessProvider = ({
  children,
  requiredRole,
  requiredAction,
  requiredGroup,
}: {
  children: ReactNode;
  requiredRole: RoleEnum[];
  requiredAction: string[];
  requiredGroup: string[];
}) => {
  const [isPermission, setIsPermission] = useState<boolean | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const isExcludedRoute = excludedRoutes.some((path) =>
    location.pathname.startsWith(path)
  );
  const useKeycloak = featureFlags.USE_KEYCLOAK;

  // Para rotas normais: dados vem do AuthDataProvider (contexto)
  const authData = useAuthData();

  // Para rotas excluidas: manter operator endpoints
  const {
    data: operatorDataPrivate,
    isLoading: operatorLoadingPrivate,
    error: operatorErrorPrivate
  } = useOperatorMeQuery(undefined, {
    skip: !isExcludedRoute || useKeycloak,
  });

  const {
    data: operatorDataPublic,
    isLoading: operatorLoadingPublic,
    error: operatorErrorPublic
  } = useOperatorMePublicQuery(undefined, {
    skip: !isExcludedRoute || !useKeycloak,
  });

  const operatorData = useKeycloak ? operatorDataPublic : operatorDataPrivate;
  const operatorLoading = useKeycloak ? operatorLoadingPublic : operatorLoadingPrivate;
  const operatorError = useKeycloak ? operatorErrorPublic : operatorErrorPrivate;

  // Selecionar fonte de dados correta, fallback para localStorage
  const apiUser = isExcludedRoute ? operatorData : authData?.user;
  const user = apiUser || getUserFromLocalStorage();
  const isLoading = isExcludedRoute ? operatorLoading : !authData?.user && authData?.isLoading;
  const error = isExcludedRoute ? operatorError : null;
  const moduleAccess = localStorage.getItem("@verzel:module");

  const group = user?.user_permission_group?.find(
    (item: any) => item.group === moduleAccess
  );

  const groupsAssignemntsToUser = user?.user_permission_group?.map(
    (item: any) => item.group
  );

  const userRole = group?.role || [];
  const assignments = group?.assignments || [];

  // Tratar erros de API apenas para rotas excluidas
  useEffect(() => {
    if (!error || !isExcludedRoute) return;

    const errorStatus = (error as any)?.status;
    const errorMessage = (error as any)?.data?.messages?.[0] || "";

    console.warn("Erro na API de operador:", { errorStatus, errorMessage, error });

    if (errorStatus === 401 || errorMessage?.includes("expired") || errorMessage?.includes("Unauthorized")) {
      dispatch(apiSlice.util.resetApiState());
      localStorage.removeItem("@azure:token");
      localStorage.removeItem("@azure:user");
      navigate("/login", { replace: true });
      return;
    }

    if (errorMessage?.includes("expired")) {
      navigate("/informations?success=false", { replace: true });
      return;
    }
  }, [error, dispatch, navigate, isExcludedRoute]);

  // Verificar permissoes
  useEffect(() => {
    if (error || isLoading) {
      return;
    }

    if (!user && !isExcludedRoute) {
      return;
    }

    if (requiredGroup && requiredGroup.length > 0) {
      // AND: o usuário precisa pertencer a TODOS os grupos exigidos.
      // Para rotas com um único grupo, equivale ao includes anterior.
      const hasAllGroups = requiredGroup.every((g) =>
        groupsAssignemntsToUser?.includes(g)
      );
      if (!hasAllGroups) {
        setIsPermission(false);
        return;
      }
    }
    const hasPermission = checkPermission({
      userRole,
      requiredAction,
      assignments,
      requiredRole,
    });

    setIsPermission(hasPermission);
  }, [
    assignments,
    navigate,
    requiredAction,
    requiredRole,
    userRole,
    error,
    user,
    isLoading,
    requiredGroup,
    groupsAssignemntsToUser,
  ]);

  // Mostrar loading enquanto carrega dados OU enquanto permissão ainda não foi verificada (null)
  if (isLoading || isPermission === null) {
    return <Load />;
  }

  if (!isPermission) {
    return <PageError status={400} message={"PÁGINA NÃO ENCONTRADA"} />;
  }

  return (
    <AccessContext.Provider value={{ isPermission: isPermission ?? false }}>
      {children}
    </AccessContext.Provider>
  );
};

export const useAccess = () => useContext(AccessContext);
